import React, { useState, useEffect } from 'react';
import { FormMode } from '@app/helpers/enums';
import { useDispatch, useSelector } from 'react-redux';
import { useTypedTranslation } from '@app/hooks';
import { getLoaderBtn, getModalSwitch } from '@app/store/actions';
import { ModalActionsComponent, PasslyTextField } from '@app/components';
import { groupsService } from '@app/services';
import { IGroup } from '@app/models';
import { genericErrorString } from '@app/helpers';

export interface IReceivedData {
    formMode: FormMode;
    groupBeingEdited?: IGroup;
    handleRefresh: Function;
}

export default function AddEditGroupDialog(props: IReceivedData) {
    const dispatch = useDispatch();
    const { translation } = useTypedTranslation();

    const [groupName, setGroupName] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    const organizationId = useSelector((state: any) => state.organizationSelect.selectedOrganization);

    useEffect(() => {
        if (props.formMode === FormMode.Edit) {
            if (props.groupBeingEdited) {
                setGroupName(props.groupBeingEdited.Name);
            }
        }
    }, []);

    const handleError = (error: any) => {
        dispatch(getLoaderBtn(false));
        setErrorMessage(genericErrorString(error));
    };

    const onFormChange = (e: any) => {
        const { name, value } = e.target;

        switch (name) {
            case 'groupName':
                setGroupName(value);
                break;
            default:
                break;
        }
    };

    const handleSubmitBtn = () => {
        dispatch(getLoaderBtn(true));

        const valid: boolean = validateForm();

        if (!valid) {
            dispatch(getLoaderBtn(false));
            return;
        }

        let json: any;

        if (props.formMode === FormMode.Add) {
            json = {
                '@odata.type': 'Kaseya.AuthAnvil.Models.Group',
                Name: groupName,
                OrganizationId: organizationId
            };

            groupsService.addGroup(json, handleError, handleSuccessAdd);
        } else if (props.formMode === FormMode.Edit) {
            if (props.groupBeingEdited) {
                json = {
                    Id: props.groupBeingEdited.Id,
                    Created: props.groupBeingEdited.Created,
                    CreatedBy: props.groupBeingEdited.CreatedBy,
                    LastModified: props.groupBeingEdited.LastModified,
                    LastModifiedBy: props.groupBeingEdited.LastModifiedBy,
                    TenantId: props.groupBeingEdited.TenantId,
                    OrganizationId: props.groupBeingEdited.OrganizationId,
                    Name: groupName,
                    SyncSource: props.groupBeingEdited.SyncSource,
                    IsSystem: props.groupBeingEdited.IsSystem
                };

                groupsService.editGroup(props.groupBeingEdited.Id, json, handleError, handleSuccessEdit);
            }
        }
    };

    const handleSuccessAdd = () => {
        props.handleRefresh(true);
        dispatch(getModalSwitch(false));
        dispatch(getLoaderBtn(false));
    };

    const handleSuccessEdit = () => {
        props.handleRefresh(groupName);
        dispatch(getModalSwitch(false));
        dispatch(getLoaderBtn(false));
    };

    const validateForm = () => {
        if (groupName === '') {
            setErrorMessage(translation('GROUPS_NAME') + ' ' + translation('IS_REQUIRED'));
            return false;
        }

        return true;
    };

    const handleCancel = () => {
        dispatch(getModalSwitch(false));
        dispatch(getLoaderBtn(false));
    };

    const okButtonText = () => {
        if (props.formMode === FormMode.Add) {
            return translation('ADD_GROUP');
        } else {
            return translation('SAVE_CHANGES');
        }
    };

    // Temporary fix, dialog UI must be the same (see 'addUser.dialog.tsx' or 'addOrganization.dialog.tsx')
    const onSubmitByEnter = (e: React.FormEvent<HTMLFormElement>) => {
        // prevent post on window.location url
        e.preventDefault();
        handleSubmitBtn();
    };

    return (
        <>
            <form className='modal__form' method='post' tabIndex={0} onSubmit={onSubmitByEnter}>
                <div className='modal__body--row'>
                        <PasslyTextField
                            id='groupName-input'
                            label={'GROUPS_NAME'}
                            name='groupName'
                            value={groupName}
                            onChange={onFormChange}
                            autoFocus
                            required
                            fullWidth
                        />

                    {errorMessage && <div className='modal__error'>{errorMessage}</div>}
                </div>
            </form>
            <ModalActionsComponent
                primaryBtn={okButtonText()}
                secondaryBtn={translation('CANCEL')}
                onClickPrimary={handleSubmitBtn}
                onClickSecondary={handleCancel}
                id='addEditGroup'
            />
        </>
    );
}
